import { isObject } from 'underscore';
import { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';

import { RootState } from '@/store/main';
import { PageModule } from '@/types/module.types';
import { saveWorkingProject } from '..';

interface IUpdateHomepageModuleReducerHelperProps {
  data: {
    module: PageModule;
  };
  state: RootState;
  dispatch: ThunkDispatch<unknown, unknown, UnknownAction>;
}

const updateHomepageModuleReducerHelper = ({ data, dispatch, state }: IUpdateHomepageModuleReducerHelperProps) => {
  const workingProject = state.project.workingProject;
  const payloadModule = data.module;
  const currentPostRegisterPage = state.project.currentPostRegisterPage;

  if (workingProject?.homepage) {
    const updateModules = (modulesToUpdate: PageModule[], updatedModule: PageModule) => {
      return modulesToUpdate.map((moduleToUpdate: PageModule) => {
        // Update in state the exact same module that was just updated in the editor
        if (moduleToUpdate.id === updatedModule.id) {
          return updatedModule;
        }

        // Same module type but different ID. Relevant for multiple modules of the same type on the same page
        // *OR* a module of the same type on a different page. If they update a speaker on the homepage, and have
        // that SAME speaker on a session page (different module, but same submodule) then the session page speaker
        // gets updated as well.
        if (moduleToUpdate.type === updatedModule.type) {
          const updatedSubmodulesMap = updatedModule.modules?.reduce(
            (acc, cur) => ({
              ...acc,
              [cur.module_id]: cur,
            }),
            {}
          );

          return {
            ...moduleToUpdate,
            modules: moduleToUpdate.modules?.map((m) => updatedSubmodulesMap[m.module_id] ?? m),
          };
        }

        // Totally unrelated module, so nothing to update here
        return moduleToUpdate;
      });
    };

    const updatedWorkingProject = {
      ...workingProject,
      status: 2,
      homepage: {
        ...workingProject.homepage,
        post_register_home_modules: updateModules(workingProject.homepage.post_register_home_modules, payloadModule),
        modules: updateModules(workingProject.homepage.modules, payloadModule),
      },
      custom_pages: workingProject.custom_pages?.map((page) => {
        if (page.path !== currentPostRegisterPage || !page.modules) return page;

        const updatedModules = updateModules(page.modules, payloadModule);

        return {
          ...page,
          modules: updatedModules,
        };
      }),
      sessions: workingProject.sessions.map((session) => ({
        ...session,
        modules: session.modules.map((sessionModule) => {
          const updatedModule = payloadModule;

          if (sessionModule.type === updatedModule.type) {
            return {
              ...sessionModule,
              modules: sessionModule.modules?.map((sessionMod: unknown) => {
                if (!updatedModule.modules || !Array.isArray(updatedModule.modules)) {
                  return sessionMod;
                }
                const matchingSubModule = updatedModule.modules.find((updatedSubmodule: unknown) => {
                  if (!isObject(updatedSubmodule) || !isObject(sessionMod)) {
                    return false;
                  }
                  return updatedSubmodule.module_id === sessionMod.module_id;
                });
                return matchingSubModule ?? sessionMod;
              }),
            };
          }

          return sessionModule;
        }),
      })),
    };

    dispatch(saveWorkingProject({ project: updatedWorkingProject, sessions: updatedWorkingProject.sessions }));
  }
};

export default updateHomepageModuleReducerHelper;
